import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import Users from '../../../graphql/queries/Users';
import UserProfileUpdate from '../../../graphql/mutations/UserProfileUpdate';
import { TextField, Button, Box, Typography, InputAdornment, IconButton } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import {useTranslation} from "react-i18next";

const ProfilePage = () => {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        currentPassword: '',
        password: '',
        passwordConfirmation: '',
    });
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [initialLoading, setInitialLoading] = useState(true);
    const [showPasswordFields, setShowPasswordFields] = useState(false);
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);

    useEffect(() => {
        // Fetch user profile
        Users
            .fetch()
            .then((data) => {
                const user = data.me;
                setFormData({
                    firstName: user.firstName || '',
                    lastName: user.lastName || '',
                    email: user.email || '',
                    password: '',
                    passwordConfirmation: '',
                });
            })
            .catch((error) => {
                console.error('Error fetching user profile:', error);
                alert('Failed to load user profile');
            })
            .finally(() => {
                setInitialLoading(false);
            });
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const validate = () => {
        const newErrors = {};

        if (!formData.firstName) newErrors.firstName = 'First name is required';
        if (!formData.lastName) newErrors.lastName = 'Last name is required';

        if (!formData.email) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Invalid email address';
        }

        const passwordsTouched = formData.password || formData.passwordConfirmation;
        if (passwordsTouched) {
            if (!formData.currentPassword) newErrors.currentPassword = 'Current password is required';
            if (!formData.password) newErrors.password = 'New password is required';
            if (!formData.passwordConfirmation) newErrors.passwordConfirmation = 'Confirm your new password';
            if (formData.password && formData.password.length < 8) newErrors.password = 'New password must be at least 8 characters';
            if (formData.password && formData.passwordConfirmation && formData.password !== formData.passwordConfirmation) newErrors.passwordConfirmation = 'Passwords do not match';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const humanizeAttribute = (attr) => {
        if (!attr) return 'Error';
        return attr
            .toString()
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (c) => c.toUpperCase());
    };

    const buildFieldErrors = (gqlErrors) => {
        // Returns: { currentPassword: "is invalid", ... }
        return (gqlErrors || []).reduce((acc, err) => {
            if (!err) return acc;
            const attribute = err.attribute || 'base';
            const message = err.message || 'is invalid';

            // Map API snake_case -> form camelCase keys
            const keyMap = {
                current_password: 'currentPassword',
                password_confirmation: 'passwordConfirmation',
                first_name: 'firstName',
                last_name: 'lastName',
            };

            const formKey = keyMap[attribute] || attribute;
            acc[formKey] = message;
            return acc;
        }, {});
    };

    const buildToastMessage = (gqlErrors) => {
        return (gqlErrors || [])
            .map((e) => `${humanizeAttribute(e.attribute)} ${e.message}`)
            .join(', ');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        const params = {
          user: {
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
          }
        };

        if (formData.password) {
          params.user.currentPassword = formData.currentPassword;
          params.user.password = formData.password;
          params.user.passwordConfirmation = formData.passwordConfirmation;
        }

        setLoading(true);

        UserProfileUpdate
            .commit(params)
            .then(({ userUpdate }) => {
                const gqlErrors = userUpdate?.errors || [];

                if (gqlErrors.length > 0) {
                    const fieldErrors = buildFieldErrors(gqlErrors);
                    setErrors((prev) => ({ ...prev, ...fieldErrors }));

                    const toastMessage = buildToastMessage(gqlErrors);
                    toast.error(`Your profile not updated: ${toastMessage}`);
                    return;
                }

                // Success: clear any previous field errors and reset password fields
                setErrors({});
                setFormData((prev) => ({
                    ...prev,
                    currentPassword: '',
                    password: '',
                    passwordConfirmation: '',
                }));
                setShowPasswordFields(false);

                toast.info('Your profile has been updated successfully');
            })
            .catch((error) => {
                console.error('Error updating user profile:', error);
                toast.error('Failed to update your profile. Please try again.');
            })
            .finally(() => {
                setLoading(false);
            });
    };

    if (initialLoading) {
        return <Typography variant="h6">Loading...</Typography>;
    }

    return (
        <Box p={4} maxWidth={600} margin="0 auto">
            <form onSubmit={handleSubmit}>
                <Box display="flex" flexDirection="column" gap={2}>
                    {/* First Name */}
                    <TextField
                        slotProps={{ inputLabel: { shrink: true } }}
                        label={t('profile.tabs.profile.firstName')}
                        variant="outlined"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        error={!!errors.firstName}
                        helperText={errors.firstName}
                        fullWidth
                    />

                    {/* Last Name */}
                    <TextField
                        slotProps={{ inputLabel: { shrink: true } }}
                        label={t('profile.tabs.profile.lastName')}
                        variant="outlined"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        error={!!errors.lastName}
                        helperText={errors.lastName}
                        fullWidth
                    />

                    {/* Email */}
                    <TextField
                        slotProps={{ inputLabel: { shrink: true } }}
                        label={t('profile.tabs.profile.email')}
                        variant="outlined"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        error={!!errors.email}
                        helperText={errors.email}
                        disabled
                        fullWidth
                    />

                    {!showPasswordFields && (
                      <Button variant="outlined" onClick={() => setShowPasswordFields(true)}>
                        {t('change_password')}
                      </Button>
                    )}

                    {showPasswordFields && (
                      <>
                        {/* Current Password */}
                        <TextField
                          slotProps={{
                            inputLabel: { shrink: true },
                            input: {
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton onClick={() => setShowCurrentPassword(v => !v)} edge="end">
                                    {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                                  </IconButton>
                                </InputAdornment>
                              )
                            }
                          }}
                          label={t('profile.tabs.profile.currentPassword', 'Current Password')}
                          variant="outlined"
                          name="currentPassword"
                          type={showCurrentPassword ? 'text' : 'password'}
                          value={formData.currentPassword}
                          onChange={handleChange}
                          error={!!errors.currentPassword}
                          helperText={errors.currentPassword}
                          fullWidth
                        />

                        {/* New Password */}
                        <TextField
                          slotProps={{
                            inputLabel: { shrink: true },
                            input: {
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton onClick={() => setShowPassword(v => !v)} edge="end">
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                  </IconButton>
                                </InputAdornment>
                              )
                            }
                          }}
                          label={t('profile.tabs.profile.newPassword')}
                          variant="outlined"
                          name="password"
                          type={showPassword ? 'text' : 'password'}
                          value={formData.password}
                          onChange={handleChange}
                          error={!!errors.password}
                          helperText={errors.password || t('profile.tabs.profile.leaveBlank')}
                          fullWidth
                        />

                        {/* Confirm New Password */}
                        <TextField
                          slotProps={{
                            inputLabel: { shrink: true },
                            input: {
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton onClick={() => setShowPasswordConfirmation(v => !v)} edge="end">
                                    {showPasswordConfirmation ? <VisibilityOff /> : <Visibility />}
                                  </IconButton>
                                </InputAdornment>
                              )
                            }
                          }}
                          label={t('profile.tabs.profile.confirmNewPassword')}
                          variant="outlined"
                          name="passwordConfirmation"
                          type={showPasswordConfirmation ? 'text' : 'password'}
                          value={formData.passwordConfirmation}
                          onChange={handleChange}
                          error={!!errors.passwordConfirmation}
                          helperText={errors.passwordConfirmation}
                          fullWidth
                        />
                      </>
                    )}

                    {/* Submit Button */}
                    <Button
                        variant="outlined"
                        color="primary"
                        type="submit"
                        disabled={loading}
                    >
                        {loading ? t('saving') : t('save_changes')}
                    </Button>
                </Box>
            </form>
        </Box>
    );
};

export default ProfilePage;
